/**
 * Saví Sava — Wishlist / Favorites (v1)
 *
 * Simple, dependency-free, localStorage-based wishlist. No backend, no
 * cross-device sync — each browser keeps its own list. Every localStorage
 * and fetch call is wrapped in try/catch so the site never breaks when
 * storage is blocked (private browsing, locked-down browsers, etc.).
 *
 * Storage: an array of product handles under STORAGE_KEY.
 *
 * Responsibilities:
 *  1. Wire up every [data-wishlist-toggle] heart button on the page
 *     (PDP media gallery, product cards, …), toggling saved state.
 *  2. On the favorites page specifically, read the saved handles and render
 *     a simple grid of saved products into #savi-favorites-list using the
 *     public /products/<handle>.js JSON endpoint (no auth needed).
 *
 * The page handle was renamed from the German /pages/favoriten to
 * /pages/favorites on 22 Sep 2026; FAVORITES_PATHS below keeps the old
 * path working for anyone arriving through a stale link or redirect.
 */
(function () {
  if (window.__savisavaWishlistInit) return;
  window.__savisavaWishlistInit = true;

  var STORAGE_KEY = 'savisava:wishlist';
  var FAVORITES_PATHS = ['/pages/favorites', '/pages/favoriten'];

  function onFavoritesPage() {
    var path = window.location.pathname;
    for (var i = 0; i < FAVORITES_PATHS.length; i++) {
      if (path.indexOf(FAVORITES_PATHS[i]) > -1) return true;
    }
    return false;
  }

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage blocked — fail silently, nothing else depends on this succeeding */
    }
  }

  function isSaved(handle) {
    return readList().indexOf(handle) > -1;
  }

  function toggleSaved(handle) {
    var list = readList();
    var index = list.indexOf(handle);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(handle);
    }
    writeList(list);
    return index === -1; /* true = now saved */
  }

  function paintButton(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    var saved = isSaved(handle);
    button.setAttribute('data-active', saved ? 'true' : 'false');
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? 'Remove from favorites' : 'Add to favorites');
  }

  function paintAllButtons(handle) {
    var selector = handle
      ? '[data-wishlist-toggle][data-product-handle="' + handle.replace(/"/g, '') + '"]'
      : '[data-wishlist-toggle]';
    document.querySelectorAll(selector).forEach(paintButton);
  }

  function onButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    var button = event.currentTarget;
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return false;
    toggleSaved(handle);
    paintAllButtons(handle);
    if (onFavoritesPage()) {
      renderFavoritesPage();
    }
    return false;
  }

  function wireButtons(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-wishlist-toggle]:not([data-wishlist-wired])');
    buttons.forEach(function (button) {
      button.setAttribute('data-wishlist-wired', 'true');
      paintButton(button);
      button.addEventListener('click', onButtonClick);
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return (
        {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[char] || char
      );
    });
  }

  function formatMoney(cents) {
    try {
      var amount = (cents / 100).toFixed(2);
      return amount;
    } catch (e) {
      return cents;
    }
  }

  function renderFavoritesPage() {
    var container = document.getElementById('savi-favorites-list');
    if (!container) return;

    var handles = readList();

    if (handles.length === 0) {
      container.innerHTML =
        '<p class="savi-favorites-empty">You haven&rsquo;t saved any favorites yet. Tap the heart on any product to keep it here.</p>';
      return;
    }

    container.innerHTML = '<p class="savi-favorites-loading">Loading your favorites&hellip;</p>';

    var fetches = handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (response) {
          if (!response.ok) throw new Error('not found');
          return response.json();
        })
        .catch(function () {
          return null;
        });
    });

    Promise.all(fetches)
      .then(function (products) {
        var valid = products.filter(Boolean);

        if (valid.length === 0) {
          container.innerHTML =
            '<p class="savi-favorites-empty">We couldn&rsquo;t load your saved favorites. Please refresh the page.</p>';
          return;
        }

        var html = '<div class="savi-favorites-grid">';
        valid.forEach(function (product) {
          var image = product.featured_image || (product.images && product.images[0]) || '';
          var price = formatMoney(product.price);
          html +=
            '<a class="savi-favorites-card" href="/products/' +
            encodeURIComponent(product.handle) +
            '">' +
            '<div class="savi-favorites-card__media">' +
            (image
              ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
              : '') +
            '<button type="button" class="wishlist-btn savi-favorites-card__remove" data-wishlist-toggle data-product-handle="' +
            escapeHtml(product.handle) +
            '" data-active="true" aria-pressed="true" aria-label="Remove from favorites">' +
            '<svg class="wishlist-btn__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 20.7s-7.6-4.6-10.2-9.5C0.2 8 2 3.9 6 3.9c2.2 0 3.8 1.2 4.4 2.8C11 5.1 12.6 3.9 14.8 3.9c4 0 5.8 4.1 4.2 7.3C19.6 16.1 12 20.7 12 20.7z" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"></path></svg>' +
            '</button>' +
            '</div>' +
            '<p class="savi-favorites-card__title">' +
            escapeHtml(product.title) +
            '</p>' +
            '<p class="savi-favorites-card__price">' +
            escapeHtml(product.price_varies ? 'From $' + price : '$' + price) +
            '</p>' +
            '</a>';
        });
        html += '</div>';

        container.innerHTML = html;
        wireButtons(container);
      })
      .catch(function () {
        container.innerHTML = '<p class="savi-favorites-empty">We couldn&rsquo;t load your saved favorites. Please refresh the page.</p>';
      });
  }

  function injectFavoritesStyles() {
    if (document.getElementById('savi-favorites-styles')) return;
    var style = document.createElement('style');
    style.id = 'savi-favorites-styles';
    style.textContent =
      '.savi-favorites-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px;}' +
      '@media screen and (min-width:750px){.savi-favorites-grid{grid-template-columns:repeat(4,1fr);}}' +
      '.savi-favorites-card{display:block;color:inherit;text-decoration:none;}' +
      '.savi-favorites-card__media{position:relative;aspect-ratio:4/5;overflow:hidden;border-radius:4px;background:#f2f2f2;margin-bottom:8px;}' +
      '.savi-favorites-card__media img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.savi-favorites-card__remove{position:absolute;top:8px;right:8px;}' +
      '.savi-favorites-card__title{font-size:0.95rem;margin:0 0 2px;}' +
      '.savi-favorites-card__price{font-size:0.9rem;color:#666;margin:0;}' +
      '.savi-favorites-empty,.savi-favorites-loading{color:#666;font-size:0.95rem;}';
    document.head.appendChild(style);
  }

  function init() {
    try {
      injectFavoritesStyles();
      wireButtons(document);

      var observer = new MutationObserver(function () {
        wireButtons(document);
      });
      observer.observe(document.body, { childList: true, subtree: true });

      if (onFavoritesPage()) {
        renderFavoritesPage();
      }
    } catch (e) {
      /* never let wishlist errors break the page */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
